import React from 'react';
import { useLocation } from 'react-router-dom';
import { calculators } from '../data/calculators';
import { SEO, CalculatorSEO } from './SEO';

const STATIC_SEO: Record<string, { title: string; description: string; keywords: string[]; canonical: string }> = {
  '/': {
    title: 'Finance Compass - Free Tools for Smart Money Decisions',
    description: 'Finance Compass provides free financial calculators for salary, taxes, budgeting, investing, and more. Fast, private, and easy to use.',
    keywords: ['finance compass', 'financial calculators', 'salary calculator', 'budget calculator', 'investment calculator', 'loan calculator', 'tax calculator'],
    canonical: '/'
  },
  '/about': {
    title: 'About Finance Compass',
    description: 'Learn more about our mission to provide free, accurate, and privacy-first financial tools.',
    keywords: ['about', 'financial tools', 'privacy-first', 'free calculators'],
    canonical: '/about'
  },
  '/privacy': {
    title: 'Privacy Policy',
    description: 'Read how we handle data, analytics, and advertising on Finance Compass.',
    keywords: ['privacy policy', 'data privacy', 'cookies', 'adsense'],
    canonical: '/privacy'
  },
  '/terms': {
    title: 'Terms of Service',
    description: 'Review the terms for using Finance Compass and our tools.',
    keywords: ['terms of service', 'terms', 'legal', 'usage'],
    canonical: '/terms'
  },
  '/contact': {
    title: 'Contact Us',
    description: 'Get in touch with the Finance Compass team for feedback and support.',
    keywords: ['contact', 'support', 'feedback', 'help'],
    canonical: '/contact'
  },
  '/dashboard': {
    title: 'Your Dashboard',
    description: 'Track your progress across calculators and goals.',
    keywords: ['dashboard', 'financial profile', 'progress'],
    canonical: '/dashboard'
  }
};

const EXTRA_CALCULATORS: Record<string, { name: string; description: string; keywords: string[] }> = {
  '/retirement-planner': {
    name: 'Retirement Planner',
    description: 'Project your retirement savings and see if you are on track to meet your goals.',
    keywords: ['retirement planner', 'retirement savings', '401k', 'financial planning']
  },
  '/goals-calculator': {
    name: 'Financial Goals',
    description: 'Define and track your financial goals with timelines and progress tracking.',
    keywords: ['financial goals', 'goal tracking', 'savings targets']
  }
};

const ADMIN_PATHS = ['/admin', '/admin/dashboard'];

export const RouteSEO: React.FC = () => {
  const location = useLocation();
  const normalizedPath = location.pathname === '/'
    ? '/'
    : location.pathname.replace(/\/$/, '');

  if (ADMIN_PATHS.some((path) => normalizedPath.startsWith(path))) {
    return (
      <SEO
        title="Admin"
        description="Admin access"
        keywords={['admin']}
        canonical={normalizedPath}
        robots="noindex, nofollow"
      />
    );
  }

  const calculatorMatch = calculators.find((calculator) => calculator.path === normalizedPath);
  if (calculatorMatch) {
    return (
      <CalculatorSEO
        calculatorName={calculatorMatch.seoTitle ?? calculatorMatch.name}
        description={calculatorMatch.seoDescription ?? calculatorMatch.description}
        keywords={calculatorMatch.seoKeywords ?? calculatorMatch.keywords}
        path={calculatorMatch.path}
      />
    );
  }

  const extraCalculator = EXTRA_CALCULATORS[normalizedPath];
  if (extraCalculator) {
    return (
      <CalculatorSEO
        calculatorName={extraCalculator.name}
        description={extraCalculator.description}
        keywords={extraCalculator.keywords}
        path={normalizedPath}
      />
    );
  }

  const staticSeo = STATIC_SEO[normalizedPath];
  if (staticSeo) {
    const robots = normalizedPath === '/dashboard' ? 'noindex, nofollow' : 'index, follow';
    return (
      <SEO
        title={staticSeo.title}
        description={staticSeo.description}
        keywords={staticSeo.keywords}
        canonical={staticSeo.canonical}
        robots={robots}
      />
    );
  }

  return (
    <SEO
      title="Finance Compass"
      description="Free financial calculators for smarter money decisions."
      keywords={['finance compass', 'financial calculators', 'personal finance', 'money tools']}
      canonical={normalizedPath}
    />
  );
};
